// Percorso file: \src\components\UserPaymentsModal.jsx
//
// MODALE «PAYMENTS» (quote del socio) — veste rifatta dal cantiere PAYMENTS,
// fetta PAY-G-BUILD, sulla voce del doc 07 §8.1 del 29 agosto 2026.
//
// ⚠️ PAY-SEASON / FETTA 2 — QUESTA FINESTRA È PER-ANNATA. Fino a qui leggeva
// e scriveva il campo piatto `fees`, cioè una sola stagione per sempre. Ora
// guarda l'annata che l'elenco sta guardando («Season Viewed»), che le arriva
// per la prop `season`, e la manda in ogni scrittura.
//
// ⚠️ È CADUTO IL SETTIMO PUNTO DELLA VESTE, quello che poteva far danno:
// l'INDICE della riga NON è più la chiave della rotta. I pagamenti si
// indirizzano per `id` (`PUT`/`DELETE /fees/:id/payments/:pagamentoId`),
// perché la posizione era un indirizzo che cambiava sotto le mani di chi lo
// usava — un `splice` per posizione su un array che si ricompatta cancellava
// la ricevuta sbagliata ogni volta che la lista a schermo non era quella
// appena riletta. Resta comunque un solo `payments.map`, nell'ordine in cui
// il backend consegna l'array: nessun ordinamento, nessun filtro, nessuna
// inversione. Ma ora è una scelta di RESA, non un vincolo di correttezza.
//
// I FOGLI, e perché sono quattro:
// - PaymentsModal.css   → la veste NUOVA di questa finestra (prefisso proprio
//                         `qpsc-pay-`, nessuna eredità);
// - AdminTools.css      → le classi CONDIVISE che non si ridichiarano: guscio
//                         §8 (.qpsc-modal*), campi §8 (.qpsc-field,
//                         .qpsc-input), spunta §8 (.qpsc-check*), pulsanti §5
//                         (.qpsc-tool-btn, .qpsc-ghost-btn);
// - MemberProfileModal.css → l'icona informativa «i» §11.3 (.qpsc-vp-info e
//                         il suo pannello .qpsc-vp-purpose), che la voce del
//                         doc 07 nomina espressamente per questa finestra;
// - UserPaymentsModal.css → il foglio VECCHIO. ⚠️ NON È UN RESIDUO E NON VA
//                         TOLTO PER PULIZIA: questa finestra non usa più
//                         nessuna delle sue classi, ma il foglio dichiara
//                         `.modal-overlay`, che altre finestre usano ancora
//                         (MemberProfileModal.jsx, Messaging.jsx)
//                         e che è dichiarata in quattro fogli diversi. Quale
//                         delle quattro vinca dipende dall'ordine del bundle
//                         CRA, che dal sorgente non si legge: toglierla di qui
//                         avrebbe potuto cambiare la resa di finestre FUORI dal
//                         perimetro di questa fetta. Si svuota nel suo giro,
//                         con la prova davanti.

import React, { useState } from 'react';
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa';
import { api } from "../api/api";
// LA SEDE UNICA DELLA LETTURA PER-ANNATA (PAY-SEASON F2): la stessa che usano
// le colonne, le card e l'export dell'elenco. Qui non se ne scrive una copia
// locale — è la regola che dice DOVE stanno le quote di un anno, e una copia
// può divergere da quella del backend.
import { quoteDellAnnata, totaleVersato } from '../utils/quote';
import { denaroAschermo } from '../utils/denaro';
import './PaymentsModal.css';
import './AdminTools.css';
import './UserPaymentsModal.css';
// ⚠️ `components/MemberProfileModal.css` — che dichiara l'icona «i» §11.3
// (.qpsc-vp-info, .qpsc-vp-info__glyph, .qpsc-vp-info--open, .qpsc-vp-purpose)
// usata qui sotto — NON si importa, e non è una dimenticanza. È già nel bundle
// unico di CRA per un'altra via (App.jsx monta MemberProfileModal, che
// importa il proprio foglio), quindi le regole ci sono comunque;
// importarlo QUI lo farebbe però raggiungere per la prima volta da
// App.jsx → UsersList → questo file, cioè PRIMA di pages/TeamAssignment.css
// invece che dopo. I due fogli dichiarano entrambi `.close-btn` con lo stesso
// peso (0,1,0) — MemberProfileModal.css fondo trasparente,
// TeamAssignment.css fondo grigio — e a parità di specificità vince
// l'ultimo emesso: l'import avrebbe cambiato la «×» di EvaluationSheetModal,
// finestra FUORI dal perimetro di questa fetta. Verificato sul CSS costruito,
// non dedotto.

// ------------------------------------------------------------------
// GLI ESTREMI DELL'ANNATA — gli stessi del backend, ricopiati qui.
//
// ⚠️ NON C'ERA UNA COSTANTE CONDIVISA DA RIUSARE: `utils/stagione.js`, la sede
// unica del metro dell'anno nel browser, dichiara il solo `annoDiLista`
// (stagione.js), che misura la GRAFIA — intero sì o no — e non
// l'intervallo. I due valori vengono da `backend/server.js`
// (`STAGIONE_MINIMA` / `STAGIONE_MASSIMA`), che `stagioneDaCorpo`
// (server.js) applica a OGNI scrittura delle rotte `/fees`.
//
// ⚠️ SONO UNA COPIA, e va detto: il browser non condivide codice col server,
// quindi se un giorno quegli estremi si muovono, si muovono in due posti.
// ------------------------------------------------------------------
const STAGIONE_MINIMA = 2020;
const STAGIONE_MASSIMA = 2100;

const formatDate = (isoDate) => {
  if (!isoDate) return '';
  const [yyyy, mm, dd] = isoDate.split('-');
  return `${dd}-${mm}-${yyyy}`;
};

// ------------------------------------------------------------------
// Le descrizioni dell'icona «i» (doc 07 §11.3, regola dei testi del 22/8/2026):
// dicono COSA È la sezione e A COSA SERVE, mai l'elenco dei campi. In inglese,
// come ogni testo a schermo (CLAUDE.md §3.7).
// ------------------------------------------------------------------
const DESCRIZIONI = {
  fees: "Where this member stands financially with the club.",
  payments: "Every payment recorded against this member's fees.",
  newPayment: "Records a payment against this member's fees."
};

// ------------------------------------------------------------------
// SEZIONE-CARD (doc 07 §8 card, §11.3 icona «i»), la stessa forma di View
// Profile: titolo ORO, piccolo, A SINISTRA, e l'icona a destra.
//
// ⚠️ L'icona è un <button>, non uno <span> con onClick: è un comando, deve
// essere raggiungibile da tastiera e annunciato da un lettore di schermo. Il
// pannello si apre AL TOCCO e si richiude al secondo tocco (§11.3: mai un
// tooltip solo-hover).
// ------------------------------------------------------------------
const Sezione = ({ chiave, titolo, aperta, onApri, children }) => (
  <section className="qpsc-pay-card">
    <div className="qpsc-pay-card__head">
      <h3 className="qpsc-pay-card__title">{titolo}</h3>

      <button
        type="button"
        className={"qpsc-vp-info" + (aperta ? " qpsc-vp-info--open" : "")}
        aria-expanded={aperta}
        aria-controls={`qpsc-pay-purpose-${chiave}`}
        aria-label={`What is “${titolo}”?`}
        onClick={() => onApri(chiave)}
      >
        <span className="qpsc-vp-info__glyph" aria-hidden="true">i</span>
      </button>
    </div>

    {aperta && (
      <p className="qpsc-vp-purpose" id={`qpsc-pay-purpose-${chiave}`}>
        {DESCRIZIONI[chiave]}
      </p>
    )}

    {children}
  </section>
);

const UserPaymentsModal = ({ user, season, onClose, onUpdate }) => {
  // ⚠️ L'ANNATA GUARDATA, e l'unica cosa che questa finestra sa dell'anno:
  // arriva dall'elenco (`stagioneAttiva`, cioè «Season Viewed») e non si
  // ricalcola qui. Una finestra che si scegliesse l'anno da sé potrebbe
  // mostrare un'annata e l'elenco dietro un'altra.
  //
  // ⚠️ ANNO VALIDO = INTERO **E** DENTRO L'INTERVALLO, lo stesso metro che il
  // backend applica in scrittura (vedi le costanti in testa al file). Il solo
  // `Number.isInteger` lasciava accesi i comandi su un anno intero ma fuori
  // intervallo, e la scrittura finiva in un 400 senza che nulla, a schermo,
  // dicesse perché.
  // ⚠️ CONSEGUENZA VOLUTA: su un anno anomalo la finestra diventa di SOLA
  // LETTURA — nessun comando di scrittura è attivo — e il titolo tace l'anno,
  // invece di esibirne uno che nessuna rotta accetterebbe.
  const annoValido =
    Number.isInteger(season) &&
    season >= STAGIONE_MINIMA &&
    season <= STAGIONE_MASSIMA;

  // Le quote da cui parte la finestra: quelle dell'ANNATA, lette con la sede
  // unica. Sull'anno storico (2026) la sede è il campo piatto e quindi si
  // legge esattamente ciò che si leggeva prima; su ogni altro anno, la voce
  // di `feesSeasons` o niente.
  const quoteIniziali = quoteDellAnnata(user, season);

  const [totalDue, setTotalDue] = useState(quoteIniziali.totalDue || 0);

  // ⚠️ RD-162(b): IL DOVUTO GIA' SALVATO, per il campo lasciato VUOTO. Il
  // campo tiene ora il testo grezzo (vedi `onChange`), e al salvataggio un
  // campo vuoto rimanda questo valore invece di `Number("") === 0`: svuotare
  // il campo non azzera piu' il dovuto. Il server rifiuterebbe un "" (400),
  // e il campo e' obbligatorio nel corpo, quindi non si puo' ometterlo.
  const [dovutoSalvato, setDovutoSalvato] = useState(quoteIniziali.totalDue || 0);
  const [payments, setPayments] = useState(quoteIniziali.payments);
  const [newDate, setNewDate] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newNote, setNewNote] = useState('');

  // ⚠️ L'INDIRIZZO DEL PAGAMENTO IN MODIFICA È IL SUO `id`, non più la sua
  // posizione: `null` = si sta aggiungendo, una stringa = si sta modificando
  // quella ricevuta. Il cambio non è cosmetico — vedi la nota in testa al
  // file: la posizione cambiava sotto le mani di chi la usava.
  const [editPaymentId, setEditPaymentId] = useState(null);

  const [specialDiscount, setSpecialDiscount] = useState(quoteIniziali.specialDiscount || false);
  const [discountNote, setDiscountNote] = useState(quoteIniziali.discountNote || '');

  // Quale descrizione «i» è aperta: è stato di VESTE — non tocca né i dati né
  // le rotte — e serve al componente Sezione qui sopra.
  const [descrizioneAperta, setDescrizioneAperta] = useState(null);

  const apriDescrizione = (chiave) =>
    setDescrizioneAperta((attuale) => (attuale === chiave ? null : chiave));

  const totalPaid = totaleVersato({ payments });
  // `Number()` perché il campo ora tiene il testo grezzo: "" vale 0 qui a
  // schermo, esattamente come prima.
  const balance = Number(totalDue) - totalPaid;

  // La ricarica dopo una scrittura, in un posto solo: si rilegge il socio e da
  // lui si riprendono i pagamenti DELL'ANNATA guardata.
  //
  // ⚠️ `GET /users/:id` RISPONDE COL DOCUMENTO GREZZO, non con la proiezione
  // di lista: le chiavi di `feesSeasons` non sono normalizzate e i pagamenti
  // storici non hanno l'`id: ""` di ripiego che la proiezione aggiunge. È
  // esattamente il motivo per cui la lettura passa da `quoteDellAnnata`, che
  // il metro dell'anno se lo porta dietro (`annoDiLista` su ciascuna chiave) e
  // vale su tutte e due le vie.
  const ricaricaEPropaga = async () => {
    const refreshed = await api.get(`/users/${user.id}`);
    setPayments(quoteDellAnnata(refreshed, season).payments);
    onUpdate(refreshed);
  };

  // ⚠️ UN PAGAMENTO SENZA `id` NON È INDIRIZZABILE, e va detto invece di
  // lasciar partire una rotta che risponderebbe 404. Le ricevute scritte prima
  // di PAY-SEASON non hanno un identificativo — la proiezione le mostra con
  // `id` vuoto e non ne inventa uno, perché un id generato in lettura
  // cambierebbe a ogni richiesta. Il rimedio esiste ed è a un clic: «Update»
  // riscrive il record e il backend timbra ogni pagamento che ne è privo.
  const senzaIndirizzo = (p) =>
    !(p && typeof p.id === 'string' && p.id.trim() !== '');

  const AVVISO_SENZA_ID =
    'This payment has no identifier yet. Press Update in the Fees section first, then try again.';

  const handleAddOrUpdatePayment = async () => {
    if (!newDate || !newAmount) return;

    // ⚠️ ANNATA IGNOTA → IL COMANDO NON PARTE. Senza un anno la rotta non
    // saprebbe dove scrivere (e risponderebbe 400): meglio non chiedere che
    // scrivere su un'annata che nessuno ha scelto. È la stessa guardia che
    // l'elenco ha già su Play Football e sul pallino Alert.
    if (!annoValido) return;

    try {
      if (editPaymentId !== null) {
        // EDIT — per ID del pagamento, nell'annata guardata.
        await api.put(`/fees/${user.id}/payments/${encodeURIComponent(editPaymentId)}`, {
          season,
          amount: Number(newAmount),
          date: newDate,
          note: newNote,
        });
      } else {
        // ADD — nell'annata guardata.
        await api.post(`/fees/${user.id}/payments`, {
          season,
          amount: Number(newAmount),
          date: newDate,
          note: newNote,
        });
      }
    } catch (err) {
      console.error("❌ Error saving payment:", err);
      alert("Failed to save payment.");
      return;
    }

    // ⚠️ RD-162(c): LA RICARICA STA FUORI DAL TRY DELLA SCRITTURA: la
    // scrittura e' riuscita, e un guasto nel rileggere non deve presentarsi
    // come «Failed to save payment.». Nessun messaggio nuovo: l'elenco resta
    // quello di prima. Stessa forma di TeamAssignment (cantiere G) e degli
    // altri due gestori di questa finestra.
    try {
      // 🔁 ricarica sempre dal backend
      await ricaricaEPropaga();
    } catch (err) {
      // Ricarica fallita: la scrittura resta riuscita, si prosegue.
    }

    // reset form
    setNewDate('');
    setNewAmount('');
    setNewNote('');
    setEditPaymentId(null);
  };




  const handleUpdateTotalDue = async () => {
    if (!annoValido) return;

    // ⚠️ RD-162(b): CAMPO VUOTO → IL DOVUTO GIA' SALVATO, non 0. Un numero
    // valido — anche negativo (credito, RD-162a) — parte com'e'.
    const campoVuoto = String(totalDue ?? '').trim() === '';
    const dovutoDaInviare = campoVuoto ? dovutoSalvato : Number(totalDue);

    try {
      const payload = {
        season,
        totalDue: dovutoDaInviare,
        specialDiscount: !!specialDiscount,
        discountNote: discountNote || ""
      };

      await api.put(`/fees/${user.id}`, payload);
    } catch (err) {
      console.error("❌ Error updating total due:", err);
      alert("Failed to save fees or discount.");
      return;
    }

    // Il valore scritto e' ora il dovuto salvato; il campo lasciato vuoto
    // torna a mostrarlo.
    setDovutoSalvato(dovutoDaInviare);
    if (campoVuoto) setTotalDue(dovutoDaInviare);

    // 🔁 ricarica dal backend. Anche i pagamenti: questa rotta riscrive il
    // record intero e, così facendo, timbra le ricevute storiche che un id
    // non ce l'avevano — dopo l'Update sono indirizzabili, e la lista a
    // schermo deve saperlo.
    // Ricarica FUORI dal try della scrittura (RD-162c, vedi sopra).
    try {
      await ricaricaEPropaga();
    } catch (err) {
      // Ricarica fallita: la scrittura resta riuscita, si prosegue.
    }

    alert("Fees updated successfully!");
  };



  const handleDeletePayment = async (p) => {
    if (!annoValido) return;

    if (senzaIndirizzo(p)) {
      alert(AVVISO_SENZA_ID);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this payment?')) return;

    try {
      // ⚠️ L'ANNO VA NELLA STRINGA DI RICERCA, non nel corpo: `api.del` non
      // manda corpo (api.js), e il backend legge `?season=` proprio per
      // questo.
      await api.del(
        `/fees/${user.id}/payments/${encodeURIComponent(p.id)}?season=${season}`
      );
    } catch (err) {
      console.error("❌ Error deleting payment:", err);
      alert("Failed to delete payment.");
      return;
    }

    // ⚠️ SE SI CANCELLA LA RICEVUTA CHE SI STA MODIFICANDO, il modulo torna
    // ad «Add»: altrimenti resterebbe aperto su un indirizzo che non esiste
    // più, e il salvataggio successivo prenderebbe un 404 senza che nulla
    // spieghi perché. Era un residuo che l'indirizzamento per POSIZIONE
    // nascondeva peggio di così: là il modulo restava aperto su una
    // posizione che nel frattempo puntava a **un'altra ricevuta**, e il
    // salvataggio la riscriveva in silenzio.
    //
    // ⚠️ E IL RESET VIENE PRIMA DELLA RICARICA, non dopo. L'id è già morto
    // nel momento in cui la DELETE risponde bene: se la rilettura fallisse
    // il pannello resterebbe appeso a quell'id, cioè proprio col 404 che
    // questo reset esiste per impedire.
    if (editPaymentId === p.id) {
      setNewDate('');
      setNewAmount('');
      setNewNote('');
      setEditPaymentId(null);
    }

    // RD-162(c): ricarica FUORI dal try della scrittura (vedi il gestore di
    // aggiunta): una cancellazione riuscita non finisce più con «Failed to
    // delete payment.» per un guasto della sola rilettura.
    try {
      // 🔁 ricarica sempre dal backend
      await ricaricaEPropaga();
    } catch (err) {
      // Ricarica fallita: la scrittura resta riuscita, si prosegue.
    }
  };

  const handleEditPayment = (p) => {
    if (senzaIndirizzo(p)) {
      alert(AVVISO_SENZA_ID);
      return;
    }

    setNewDate(p.date);
    setNewAmount(p.amount);
    setNewNote(p.note || '');
    setEditPaymentId(p.id);
  };


  return (
    <div className="qpsc-modal-overlay">
      <div className="qpsc-modal qpsc-pay-modal">
        {/* §8: il titolo della modale è quello del guscio — chiaro e centrato,
            mai oro pieno. Il «Close» non sta più qui: è sceso fra i comandi in
            fondo, dove il guscio li tiene fermi. */}
        {/* ⚠️ L'ANNATA STA NEL TITOLO (PAY-SEASON F2, decisione del committente
            dopo la revisione). Non è un vezzo: da questa fetta la finestra
            LEGGE e SCRIVE un anno preciso, e prima non lo diceva da nessuna
            parte. Chi avesse spostato «Season Viewed» avrebbe visto un record
            vuoto su un socio che aveva pagato — che somiglia a una perdita di
            dati — e avrebbe potuto registrare una ricevuta in un'annata che non
            credeva di guardare.
            ⚠️ Con annata IGNOTA non si scrive «— null»: si tace, e sono i
            comandi a dire che non sono disponibili (vedi `annoValido`). */}
        <h2 className="qpsc-modal__title">
          Payments for {user.firstName} {user.lastName}
          {annoValido ? ` — ${season}` : ''}
        </h2>

        {/* Il corpo è l'unica parte che scorre, senza barra visibile. */}
        <div className="qpsc-modal__body">

          <Sezione
            chiave="fees"
            titolo="Fees"
            aperta={descrizioneAperta === "fees"}
            onApri={apriDescrizione}
          >
            <div className="qpsc-pay-fields">
              <div className="qpsc-field">
                <label className="qpsc-field__label" htmlFor="qpsc-pay-total-due">
                  Total Fees Due
                </label>
                {/* ⚠️ `type="number"` e la conversione `Number()` SONO funzione:
                    la veste non tocca né l'uno né l'altra. RD-162(b): la
                    conversione non sta più qui ma al salvataggio, dove un
                    campo vuoto si riconosce ancora. */}
                <input
                  id="qpsc-pay-total-due"
                  className="qpsc-input qpsc-pay-input"
                  type="number"
                  value={totalDue}
                  onChange={(e) => setTotalDue(e.target.value)}
                />
              </div>

              {/* §8 spunta del kit: l'<input> resta l'elemento che porta
                  `checked`/`onChange`; il quadrato è uno <span> decorativo. */}
              <label className="qpsc-check">
                <input
                  className="qpsc-check__input"
                  type="checkbox"
                  checked={specialDiscount}
                  onChange={(e) => setSpecialDiscount(e.target.checked)}
                />
                <span className="qpsc-check__box" aria-hidden="true" />
                <span>Special Discount</span>
              </label>

              {/* Il campo della nota ESISTE SOLO se la spunta è attiva: è
                  comportamento, non veste, e resta com'era. */}
              {specialDiscount && (
                <div className="qpsc-field">
                  <label className="qpsc-field__label" htmlFor="qpsc-pay-discount-note">
                    Discount note
                  </label>
                  <input
                    id="qpsc-pay-discount-note"
                    className="qpsc-input qpsc-pay-input"
                    type="text"
                    placeholder="Enter discount note"
                    value={discountNote}
                    onChange={(e) => setDiscountNote(e.target.value)}
                  />
                </div>
              )}

              <div className="qpsc-pay-totals">
                <p className="qpsc-pay-total">
                  <span className="qpsc-pay-total__label">Total Paid</span>
                  <span className="qpsc-pay-total__value">{denaroAschermo(totalPaid)}</span>
                </p>
                <p className="qpsc-pay-total">
                  <span className="qpsc-pay-total__label">Balance</span>
                  {/* Deroga «denaro per segno» (§1.5, voce del 29/8): rosso se
                      dovuto, verde se saldato. ⚠️ Cambia la STRINGA della
                      classe, non la condizione. */}
                  <span
                    className={
                      "qpsc-pay-total__value " +
                      (balance > 0
                        ? "qpsc-pay-total__value--due"
                        : "qpsc-pay-total__value--settled")
                    }
                  >
                    {denaroAschermo(balance)}
                  </span>
                </p>
              </div>

              {/* ⚠️ Update salva INSIEME dovuto, spunta e nota (un solo
                  `PUT /fees/:id`): sta in fondo alla sezione perché è il
                  comando di tutti e tre, non del solo campo qui sopra. */}
              <div className="qpsc-pay-actions">
                {/* ⚠️ SENZA UN'ANNATA IL COMANDO È DISABILITATO, non muto
                    (decisione del committente dopo la revisione). La guardia
                    nel gestore resta — è lei che impedisce la scrittura — ma
                    un bottone che si preme e non fa nulla è peggio di un
                    bottone che dice di non essere disponibile. Lo stile del
                    disabilitato è quello del kit (§5, AdminTools.css):
                    opacità 50%, mai un colore diverso. */}
                <button
                  type="button"
                  className="qpsc-tool-btn"
                  onClick={handleUpdateTotalDue}
                  disabled={!annoValido}
                >
                  Update
                </button>
              </div>
            </div>
          </Sezione>

          <Sezione
            chiave="payments"
            titolo="Payments"
            aperta={descrizioneAperta === "payments"}
            onApri={apriDescrizione}
          >
            {/* ⚠️ UN SOLO DOM PER LE DUE RESE. Sopra i 768px questo è una
                tabella; sotto, le stesse righe diventano card impilate e le
                intestazioni di colonna riappaiono come etichetta di cella
                (`data-label`). Non esistono due liste, e la ragione oggi è
                solo la resa: da PAY-SEASON F2 l'indirizzo del dato è l'`id`
                del pagamento, non più la sua posizione. */}
            <table className="qpsc-pay-table">
              <thead className="qpsc-pay-table__head">
                <tr>
                  <th className="qpsc-pay-table__col" scope="col">Date</th>
                  <th className="qpsc-pay-table__col" scope="col">Amount</th>
                  <th className="qpsc-pay-table__col" scope="col">Note</th>
                  <th className="qpsc-pay-table__col" scope="col">Actions</th>
                </tr>
              </thead>
              <tbody className="qpsc-pay-table__body">
                {/* ⚠️ LA CHIAVE DI REACT È L'`id` DEL PAGAMENTO. Il ripiego
                    sulla posizione vale per le sole ricevute storiche, che un
                    id non ce l'hanno: senza, due voci senza id avrebbero la
                    stessa chiave vuota e React le confonderebbe fra loro. */}
                {payments.map((p, index) => (
                  <tr
                    key={p?.id || `senza-id-${index}`}
                    className="qpsc-pay-table__row"
                  >
                    <td className="qpsc-pay-table__cell" data-label="Date">
                      {formatDate(p.date)}
                    </td>
                    <td className="qpsc-pay-table__cell" data-label="Amount">
                      {denaroAschermo(p.amount)}
                    </td>
                    <td className="qpsc-pay-table__cell" data-label="Note">
                      {p.note}
                    </td>
                    <td
                      className="qpsc-pay-table__cell qpsc-pay-table__cell--actions"
                      data-label="Actions"
                    >
                      {/* §5 «Icone»: Font Awesome, mai emoji. Un pulsante di
                          sola icona porta l'`aria-label`, o non dice nulla a un
                          lettore di schermo. */}
                      {/* ⚠️ ANCHE QUESTE DUE SONO DISABILITATE SENZA ANNATA,
                          come «Update» e «Add»: sono comandi di SCRITTURA, e
                          la regola del committente («niente bottoni muti»)
                          vale per tutti e quattro, non per due su quattro.
                          ⚠️ NON SI ACCAVALLA con la guardia del pagamento
                          SENZA `id` (`senzaIndirizzo`): quella è un'altra
                          condizione — anno VALIDO, ricevuta vecchia non
                          indirizzabile — e resta com'era, icone vive e
                          messaggio al clic. Qui `disabled` scatta solo quando
                          l'anno non è valido. */}
                      <span className="qpsc-pay-tools">
                        <button
                          type="button"
                          className="qpsc-ghost-btn qpsc-pay-icon-btn"
                          aria-label="Edit this payment"
                          onClick={() => handleEditPayment(p)}
                          disabled={!annoValido}
                        >
                          <FaPencilAlt aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="qpsc-ghost-btn qpsc-pay-icon-btn"
                          aria-label="Delete this payment"
                          onClick={() => handleDeletePayment(p)}
                          disabled={!annoValido}
                        >
                          <FaTrashAlt aria-hidden="true" />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Sezione>

          {/* Il titolo della sezione e il testo del bottone dipendono da
              `editPaymentId`: l'espressione condizionale è quella di prima,
              su un indirizzo che non è più la posizione. */}
          <Sezione
            chiave="newPayment"
            titolo={editPaymentId !== null ? 'Edit Payment' : 'Add New Payment'}
            aperta={descrizioneAperta === "newPayment"}
            onApri={apriDescrizione}
          >
            <div className="qpsc-pay-fields">
              <div className="qpsc-field">
                <label className="qpsc-field__label" htmlFor="qpsc-pay-date">
                  Date
                </label>
                <input
                  id="qpsc-pay-date"
                  className="qpsc-input qpsc-pay-input"
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                />
              </div>

              <div className="qpsc-field">
                <label className="qpsc-field__label" htmlFor="qpsc-pay-amount">
                  Amount
                </label>
                <input
                  id="qpsc-pay-amount"
                  className="qpsc-input qpsc-pay-input"
                  type="number"
                  placeholder="Amount"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                />
              </div>

              <div className="qpsc-field">
                <label className="qpsc-field__label" htmlFor="qpsc-pay-note">
                  Note
                </label>
                <input
                  id="qpsc-pay-note"
                  className="qpsc-input qpsc-pay-input"
                  type="text"
                  placeholder="Note"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />
              </div>

              <div className="qpsc-pay-actions">
                {/* Stessa ragione del comando «Update» qui sopra. */}
                <button
                  type="button"
                  className="qpsc-tool-btn"
                  onClick={handleAddOrUpdatePayment}
                  disabled={!annoValido}
                >
                  {editPaymentId !== null ? 'Save' : 'Add'}
                </button>
              </div>
            </div>
          </Sezione>

        </div>

        {/* §5.3: «Close» è un'azione di servizio, quindi fantasma; sta nei
            comandi del guscio, che restano fermi mentre il corpo scorre.
            `onClose` è identico a prima. */}
        <div className="qpsc-modal__actions">
          <button type="button" className="qpsc-ghost-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserPaymentsModal;
